import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { MessageService } from 'primeng/components/common/messageservice';
import { Toro } from '../api/common/enumerations';
import { GatewayDiscover } from '../api/models/gateway/gateway-discover.model';
import { Constants } from '../core/constants';

@Injectable()
export class UtilityService {
    constructor(private messageService: MessageService) { }

    static dateToMinutesOfToday(date: Date): number {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        return hours * 60 + minutes;
    }

    static minutesToDateOfToday(totalMinutes: number): Date {
        const today = new Date();
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes - (hours * 60);
        return new Date(today.getFullYear(), today.getMonth(), today.getDay(), hours, minutes);
    }

    static secondsToTimeString(totalSeconds: number): string // 335 seconds return in format of 05:35
    {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        return (hours < 1) ? `${minutes}:${('0' + seconds).slice(-2)}` : `${hours}:${('0' + minutes).slice(-2)}:${('0' + seconds).slice(-2)}`;
    }

    static getStationRuntimeString(totalSeconds): string {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.round(totalSeconds / 60);

        return (hours === 0 && minutes === 0) ? '< 1m' : `${('0' + hours).slice(-2)}h ${('0' + minutes).slice(-2)}m`;
    }

    static convertBooleanArrayToByte(booleanArray: boolean[]): number {
        let byte = 0;

        for (let i = 0; i < booleanArray.length; i++) {
            if (booleanArray[i]) {
                byte = byte | (1 << i);
            }
        }

        return byte;
    }

    static convertByteToBooleanArray(byte: number, booleanArrayLength: number): boolean[] {
        const booleanArray = [];

        for (let i = 0; i < booleanArrayLength; i++) {
            booleanArray[i] = (byte & (1 << i)) !== 0;
        }

        booleanArray.reverse();

        return booleanArray;
    }

    static hexToDecimal(hexValue: string, deviceType): number {
        if(deviceType == Toro.DeviceType.EVO || deviceType == Toro.DeviceType.EVOAG)
            return parseInt(hexValue.toString().replace('002409020B', ''), 16);
        else //(deviceType == Toro.DeviceType.LXCEXP)
            return parseInt(hexValue.toString().replace('00240902', ''), 16);
    }

    hexToDecDeviceId(notification) {
        let hexId, deviceId;
        if (notification && notification.UId) {
            hexId = notification.UId.replace("00240902", "");
            deviceId = parseInt(hexId, 16);
        }
        return deviceId;
    }

    //fullyQualifiedUId		(hex) The fully qualified UID, includind Toro's IEEE OUI (eg. 002409020C000001)
    //smartUId              (hex) Returns the fully qualified UID excepting Toro's IEEE OUI (eg. 0C000001)
    //dumbUId				(hex) Returns only the portion of the UId representing the device serial number (eg. 000001)
    //deviceType				  Gives the device type that the UID correspond to
    //lenghtOfSmartUId            8
    //deviceTypeLength            2
    static getDeviceTypeFromUid(fullyQualifiedUId: string): Toro.DeviceType {
        const lenghtOfSmartUId = 8;
        const deviceTypeLength = 2;

        const hexString = fullyQualifiedUId.slice(lenghtOfSmartUId, lenghtOfSmartUId + deviceTypeLength);
        let deviceType = <Toro.DeviceType>parseInt(hexString, 16)
        return deviceType;
    }

    static getShortUIdFromFullUId(fullUId: string): string {
        return fullUId.substr(fullUId.length - 6);
    }

    static isEmptyObject(obj: object): boolean {
        return Object.keys(obj).length === 0 && obj.constructor === Object;
    }

    static isEmptyArray(array): boolean {
        if (array === undefined || array.length == 0) {
            // array does not exist or is empty
            return true
        }
        return false;
    }

    static areArraysEqual(array1: any[], array2: any[]): boolean {
        if (array1.length !== array2.length) {
            return false;
        }

        for (let i = 0; i < array1.length; i++) {
            if (array1[i] !== array2[i]) {
                return false;
            }
        }

        return true;
    }

    static isOnline(whenLastKnownCommunication): boolean {
        const onlineThresholdInMinutes = 60;
        const now = moment();
        const timeDifference = moment.duration(now.diff(moment(whenLastKnownCommunication)));

        return timeDifference.asMinutes() < onlineThresholdInMinutes;
    }

    static getDeviceStatusText(whenLastKnownCommunication): string {
        const now = moment.utc();
        const timeDifference = moment.duration(now.diff(moment(whenLastKnownCommunication)));

        if (timeDifference.asDays() > 10) {
            return 'unk';
        }
        else if (timeDifference.asMinutes() < 2) {
            return 'just now';
        }
        else if (timeDifference.asHours() < 1) {
            return `${Math.floor(timeDifference.asMinutes())} min ago`;
        }
        else if (timeDifference.asDays() < 1) {
            return `${Math.floor(timeDifference.asHours())} hrs ${Math.floor(timeDifference.asMinutes()) % 60} min ago`;
        }
        else if (timeDifference.asDays() === 1) {
            return '1 day ago';
        }
        else {
            return `${Math.floor(timeDifference.asDays())} days ago`;
        }
    }

    static getTotalDaysOfYear() {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 0);

        const diff = (now.getTime() - start.getTime()) + ((start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000);
        return Math.floor(diff / 1000 / 60 / 60 / 24);
    }

    static getTotalMinutesOfDay() {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0);

        return Math.floor((now.getTime() - start.getTime()) / 1000 / 60);
    }

    public copyToClipboard(stringToCopy: string) {
        const textArea = document.createElement('textarea');
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.opacity = '0';
        textArea.value = stringToCopy;
        document.body.appendChild(textArea);
        textArea.select();

        const successCopyToClipboard = document.execCommand('copy');
        if (successCopyToClipboard) {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Secret ID has been copied to clipboard.' });
        }
        else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to copy the Secret ID to clipboard.' });
        }
    }

    static isProgrammableController(deviceType: Toro.DeviceType) {
        switch (deviceType) {
            case Toro.DeviceType.EVO:
            case Toro.DeviceType.KD2:
            case Toro.DeviceType.RD:
            case Toro.DeviceType.LXCEXP:
                return true;

            default:
                return false;
        }
    }

    static isClimateLogicController(deviceType: Toro.DeviceType) {
        switch (deviceType) {
            case Toro.DeviceType.EVO:
            case Toro.DeviceType.EVOAG:
            case Toro.DeviceType.LXCEXP:
                return false;

            default:
                return true;
        }
    }

    static isSmartConnectController(deviceType: Toro.DeviceType) {
        switch (deviceType) {
            case Toro.DeviceType.EVO:
            case Toro.DeviceType.EVOAG:
            case Toro.DeviceType.LXCEXP:
                return true;

            default:
                return false;
        }
    }

    static generateUniqueLightingPin(): number {
        return Math.floor(1000 + Math.random() * 9000);
    }

    static pad(num: number, leadingZeroes: number = 2) {
        const padding = '0'.repeat(leadingZeroes);

        const paddedNum = padding + num;
        return paddedNum.substr(paddedNum.length - leadingZeroes);
    }

    //for CCT colors, send reduced brightness to backend
    setBrightness(brightness: any, colorType: number, isWellLight: boolean): number {
        let themeDimming;

        if (brightness <= 5) {
            themeDimming = 5;
        }
        else {
            if (isWellLight && (colorType == Constants.CCTColorType || colorType == Constants.DefaultCCTColorType)) {
                //Well(Monet Light)
                themeDimming = (brightness * 90) / 100;
            } else {
                //Rembrandt (Up/Down Light) OR Van Gogh (Area Light)
                themeDimming = brightness >= 100 ? 100 : brightness;
            };
        }
        return Math.round(themeDimming);
    }

    //for CCT colors, get brightness from backend and set in UI
    getBrightness(themeDimming: any, colorType: number, isWellLight: boolean): number {
        let brightness;
        if (themeDimming == 5) {
            brightness = Constants.brightnessMin;
        }
        else {
            if (isWellLight && (colorType == Constants.CCTColorType || colorType == Constants.DefaultCCTColorType)) {
                //Well(Monet Light)
                let calculatedThemeDimming = (themeDimming / 90) * 100;
                brightness = calculatedThemeDimming >= 100 ? 100 : calculatedThemeDimming;
            }
            else {
                //Rembrandt (Up/Down Light) OR Van Gogh (Area Light)
                brightness = themeDimming;
            };
        }

        return Math.round(brightness);
    }

    //for RGB colors, calculate brightness based on current used and save to backend
    calculateBrightnessByCurrent(brightness: number, fixtureType: number, rgb: any): number {
        let currentUsed = (rgb.r + rgb.g + rgb.b) * 0.54 / 255;
        let actualBrightness = brightness;
        if (fixtureType == Toro.FixtureType['Well Light'] || fixtureType == Toro.FixtureType['Well Light(Adjustable Beam)']) {
            if (currentUsed > 0.9) {
                let maxBrightness = (0.9 / currentUsed) * 100;
                actualBrightness = (maxBrightness / 100) * brightness;
            }
        } else {
            if (currentUsed > 1) {
                let maxBrightness = (1 / currentUsed) * 100;
                actualBrightness = (maxBrightness / 100) * brightness;
            }
        }
        let dimming = actualBrightness >= 100 ? 100 : actualBrightness;
        return Math.round(dimming);
    }

    //for RGB colors, calculate user selected brightness to display on UI
    getUserSelectedBrightness(brightness: number, fixtureType: number, rgb: any): number {
        let currentUsed = (rgb.r + rgb.g + rgb.b) * 0.54 / 255;
        let actualBrightness = brightness;
        if (fixtureType == Toro.FixtureType['Well Light'] || fixtureType == Toro.FixtureType['Well Light(Adjustable Beam)']) {
            if (currentUsed > 0.9) {
                let maxBrightness = (0.9 / currentUsed) * 100;
                actualBrightness = (brightness / maxBrightness) * 100;
            }
        } else {
            if (currentUsed > 1) {
                let maxBrightness = (1 / currentUsed) * 100;
                actualBrightness = (brightness / maxBrightness) * 100;
            }

        }
        let dimming = actualBrightness >= 100 ? 100 : actualBrightness;
        return Math.round(dimming);
    }


    onOwnedGatewayDiscovered(discoveredGateway: GatewayDiscover, contextRef: any) {
        //close gateway in DiscoveryMode Dialog
        contextRef.gatewayInDiscoveryModeDialogIsDisplayed = false;
        if (contextRef.activeModal) {
            contextRef.activeModal.close();
        }

        contextRef.gatewayDiscover = discoveredGateway;

        contextRef.discoveryDialogIsDisplayed = true;
        contextRef.gatewayDiscoveryDetectedDialogRef = contextRef.modalService.open(contextRef.myComponent, { backdrop: 'static', keyboard: false, centered: true/* , container:contextRef.id */ });
        contextRef.gatewayDiscoveryDetectedDialogRef.componentInstance.accountId = contextRef.accountId;
        contextRef.gatewayDiscoveryDetectedDialogRef.componentInstance.gatewayAddOrEditMode = contextRef.gatewayAddOrEditMode;
        contextRef.gatewayDiscoveryDetectedDialogRef.componentInstance.gatewayDiscover = contextRef.gatewayDiscover;
        return contextRef.gatewayDiscoveryDetectedDialogRef;
    }

    displayGatewayInDiscoveryModeDialog(contextRef: any) {
        contextRef.gatewayInDiscoveryModeDialogIsDisplayed = true;
        contextRef.gatewayDiscoveryDialogRef = contextRef.modalService.open(contextRef.myComponent, { backdrop: 'static', keyboard: false, centered: true, container: contextRef.id });
        contextRef.gatewayDiscoveryDialogRef.componentInstance.accountId = contextRef.accountId;
        contextRef.gatewayDiscoveryDialogRef.componentInstance.locationId = contextRef.locationId;
        contextRef.gatewayDiscoveryDialogRef.componentInstance.preOwnedGatewayId = contextRef.preownedGateway.Id;
        contextRef.gatewayDiscoveryDialogRef.componentInstance.gatewayAddOrEditMode = contextRef.gatewayAddOrEditMode;
        return contextRef.gatewayDiscoveryDialogRef;
    }

    cloneObj(obj) {
        return JSON.parse(JSON.stringify(obj));
    }

    sortByDateDesc(array: any[], columnToSort: string) {
        return array.sort((a: any, b: any) => {
            return <any>new Date(b[columnToSort]) - <any>new Date(a[columnToSort]);
        });
    }

    sortByDateAsc(array: any[], columnToSort: string) {
        return array.sort((a: any, b: any) => {
            return <any>new Date(a[columnToSort]) - <any>new Date(b[columnToSort]);
        });
    }


    getImage(type) {
        let fixtureImage;
        let path = Constants.imageBasePath + 'fixtures/';

        switch (type) {
            case Toro.FixtureType['Area Light']:
                fixtureImage = path + "fixture3.png";
                break;
            case Toro.FixtureType.Auto:
                fixtureImage = path + "fixture6.png";
                break;
            case Toro.FixtureType['Down Light']:
            case Toro.FixtureType['Down Light(Adjustable Beam)']:
                fixtureImage = path + "fixture2.png";
                break;
            case Toro.FixtureType['Up Light']:
            case Toro.FixtureType['Up Light(Adjustable Beam)']:
                fixtureImage = path + "fixture1.png";
                break;
            case Toro.FixtureType['Well Light']:
            case Toro.FixtureType['Well Light(Adjustable Beam)']:
                fixtureImage = path + "fixture6.png";
                break;
        }

        return fixtureImage;
    }

    isValidRGB(rbgToAdd: number[]): boolean {
        let kelvinTable = Constants.kelvinTable;
        let minCCTIndex = kelvinTable.findIndex(x => x.kelvin === Constants.kelvinMin);
        let maxCCTIndex = kelvinTable.findIndex(x => x.kelvin === Constants.kelvinMax);

        // if rgb color array is found in kelvin table and within allowed min/max kelvin values then
        // return false; otherwise, return true for isValidRGB

        let CCTColor = kelvinTable.find((ele, index) => (ele.rgb.toString() === rbgToAdd.toString() &&
            (index > minCCTIndex && index < maxCCTIndex)));
        return CCTColor == undefined;
    }

    checkIsWellLight(fixtureType) {
        let checkIsWellLight = false;
        if (fixtureType == Toro.FixtureType['Well Light'] ||
            fixtureType == Toro.FixtureType['Well Light(Adjustable Beam)']) {
            checkIsWellLight = true;
        } else {
            checkIsWellLight = false;
        }
        return checkIsWellLight;
    }

    checkIfBeamspread(fixtureType) {
        let isBeamSpread = false;
        if (fixtureType == Toro.FixtureType['Area Light'] ||
            fixtureType == Toro.FixtureType['Up Light'] ||
            fixtureType == Toro.FixtureType['Down Light'] ||
            fixtureType == Toro.FixtureType['Well Light']) {
            isBeamSpread = false;
        } else {
            isBeamSpread = true;
        }
        return isBeamSpread;
    }

    getTime(seconds) {
        let hours;
        let minutes = seconds ? seconds / 60 : 0;
        if (minutes < 60) {
            hours = 0;
        }
        else {
            hours = minutes / 60;
            minutes = minutes % 60;
        }
        const today = new Date();
        return new Date(today.getFullYear(), today.getMonth(), today.getDay(), +hours, +minutes);
    }

    getTimeFromMinutes(min: number) {
        const hours = +(min / 60);
        const minutes = min % 60;
        const today = new Date();
        return new Date(today.getFullYear(), today.getMonth(), today.getDay(), hours, +minutes);
    }

    timePickerValueToMinutes(timePickerValue: Date): number {
        const hours = timePickerValue.getHours();
        const minutes = timePickerValue.getMinutes();
        return (hours * 60) + minutes;
    }
}
